import React from 'react';

export type StatusVariant =
  | 'ready'
  | 'processing'
  | 'failed'
  | 'folder'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

interface StatusBadgeProps {
  variant: StatusVariant;
  children?: React.ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

const getVariantStyles = (variant: StatusVariant): string => {
  const baseStyles =
    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium';

  switch (variant) {
    case 'ready':
    case 'success':
      return `${baseStyles} bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400`;
    case 'processing':
    case 'warning':
      return `${baseStyles} bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400`;
    case 'failed':
    case 'error':
      return `${baseStyles} bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400`;
    case 'folder':
    case 'info':
      return `${baseStyles} bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400`;
    default:
      return `${baseStyles} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300`;
  }
};

const getSizeStyles = (size: 'sm' | 'md'): string => {
  switch (size) {
    case 'sm':
      return 'px-2 py-1 text-xs';
    case 'md':
      return 'px-3 py-1 text-sm';
    default:
      return 'px-2 py-1 text-xs';
  }
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  children,
  size = 'sm',
  className = '',
}) => {
  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  // Replace base padding with size-specific padding
  const finalStyles = variantStyles.replace('px-2 py-1', sizeStyles);

  return (
    <span className={`${finalStyles} ${className}`}>{children || variant}</span>
  );
};

export default StatusBadge;
